import { ApplicationInsights } from "@microsoft/applicationinsights-web";

const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;
const telemetryEnabled = process.env.REACT_APP_APPINSIGHTS_ENABLED !== "false";
const environmentName = process.env.REACT_APP_MODE || process.env.NODE_ENV || "development";
const cloudRole = process.env.REACT_APP_APPINSIGHTS_CLOUD_ROLE || "agic-frontend";
const appVersion = process.env.REACT_APP_VERSION || "unknown";
const correlationHeaderExcludedDomains = [
	"client.crisp.chat",
	"*.crisp.chat",
	"cdn.livechatinc.com",
	"*.livechatinc.com",
];

let appInsightsInstance = null;
let isInitialized = false;

function isConfigured() {
	return telemetryEnabled && Boolean(connectionString);
}

function getAppInsights() {
	if (!isConfigured()) {
		return null;
	}

	if (!appInsightsInstance) {
		appInsightsInstance = new ApplicationInsights({
			config: {
				connectionString,
				enableAutoRouteTracking: false,
				autoTrackPageVisitTime: true,
				disableAjaxTracking: false,
				disableFetchTracking: false,
				disableExceptionTracking: false,
				enableUnhandledPromiseRejectionTracking: true,
				enableCorsCorrelation: true,
				correlationHeaderExcludedDomains,
			},
		});
	}

	return appInsightsInstance;
}

function enrichTelemetry(envelope) {
	if (!envelope) {
		return;
	}

	envelope.tags = envelope.tags || {};
	envelope.tags["ai.cloud.role"] = cloudRole;

	const baseData = envelope.data?.baseData;
	if (!baseData || typeof baseData !== "object") {
		return;
	}

	baseData.properties = {
		...(baseData.properties || {}),
		environment: environmentName,
		appVersion,
	};
}

function normalizeTelemetryIdentifier(value) {
	if (value === null || value === undefined) {
		return "";
	}

	return String(value).trim();
}

function resolveAuthenticatedUserId(user = {}, fallbackUserId = "") {
	const candidates = [
		fallbackUserId,
		user?.global_name_id,
		user?.userId,
		user?.user_id,
		user?.id,
	];

	return candidates.map(normalizeTelemetryIdentifier).find(Boolean) || "";
}

function resolveAccountId(user = {}) {
	const candidates = [
		user?.account_id,
		user?.accountId,
		user?.client_global_name_id,
		user?.company_id,
	];

	return candidates.map(normalizeTelemetryIdentifier).find(Boolean) || undefined;
}

function initAppInsights() {
	const appInsights = getAppInsights();

	if (!appInsights || isInitialized) {
		return appInsights;
	}

	appInsights.loadAppInsights();
	appInsights.addTelemetryInitializer(enrichTelemetry);
	isInitialized = true;

	return appInsights;
}

function buildAbsoluteUrl(path) {
	if (typeof window === "undefined") {
		return path;
	}

	if (/^https?:\/\//.test(path)) {
		return path;
	}

	return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
}

function trackPageView(path) {
	const appInsights = initAppInsights();

	if (!appInsights || !path) {
		return;
	}

	appInsights.trackPageView({
		name: typeof document !== "undefined" && document.title ? document.title : path,
		uri: buildAbsoluteUrl(path),
	});
}

function trackEvent(name, properties = {}, measurements = {}) {
	const appInsights = initAppInsights();

	if (!appInsights || !name) {
		return;
	}

	appInsights.trackEvent({ name, properties, measurements });
}

function trackTrace(message, properties = {}, severityLevel) {
	const appInsights = initAppInsights();

	if (!appInsights || !message) {
		return;
	}

	appInsights.trackTrace({ message, severityLevel }, properties);
}

function trackException(error, properties = {}) {
	const appInsights = initAppInsights();

	if (!appInsights || !error) {
		return;
	}

	const exception = error instanceof Error ? error : new Error(String(error));
	appInsights.trackException({ exception }, properties);
}

function setAuthenticatedUserContext(userId, accountId) {
	const appInsights = initAppInsights();

	if (!appInsights || !userId) {
		return;
	}

	appInsights.setAuthenticatedUserContext(String(userId), accountId ? String(accountId) : undefined, true);
}

function clearAuthenticatedUserContext() {
	const appInsights = initAppInsights();

	if (!appInsights) {
		return;
	}

	appInsights.clearAuthenticatedUserContext();
}

function syncAuthenticatedUserContext({ loggedIn, user, userId }) {
	if (!loggedIn) {
		clearAuthenticatedUserContext();
		return;
	}

	const authenticatedUserId = resolveAuthenticatedUserId(user, userId);

	if (!authenticatedUserId) {
		return;
	}

	setAuthenticatedUserContext(authenticatedUserId, resolveAccountId(user));
}

export {
	clearAuthenticatedUserContext,
	correlationHeaderExcludedDomains,
	getAppInsights,
	initAppInsights,
	setAuthenticatedUserContext,
	syncAuthenticatedUserContext,
	trackEvent,
	trackException,
	trackPageView,
	trackTrace,
};
